import { Queue } from './Queue';
/**
 * Generic directed graph data structure.
 */
export class Graph {
    constructor() {
        this.forwardAdj = new Map();
        this.backwardAdj = new Map();
    }
    /**
     * Get all nodes in the graph.
     *
     * Use .has() to check if a node exists as searching this list takes O(V).
     *
     * @returns
     */
    get nodes() {
        return [...this.forwardAdj.keys()];
    }
    /**
     * Get all edges in the graph [from, to].
     *
     * @returns
     */
    get edges() {
        const edges = [];
        this.forwardAdj.forEach((children, node) => {
            for (const child of children) {
                edges.push([node, child]);
            }
        });
        return edges;
    }
    /**
     * Get the reverse edges in the graph [to, from].
     *
     * @returns
     */
    get reverseEdges() {
        const edges = [];
        this.backwardAdj.forEach((parents, node) => {
            for (const parent of parents) {
                edges.push([node, parent]);
            }
        });
        return edges;
    }
    /**
     * Get the sources of the graph (in-degree 0).
     *
     * @returns
     */
    get sources() {
        const sources = [];
        this.backwardAdj.forEach((parents, node) => {
            if (parents.length === 0) {
                sources.push(node);
            }
        });
        return sources;
    }
    /**
     * Get the sinks of the graph (out-degree 0).
     *
     * @returns
     */
    get sinks() {
        const sinks = [];
        this.forwardAdj.forEach((children, node) => {
            if (children.length === 0) {
                sinks.push(node);
            }
        });
        return sinks;
    }
    /**
     * Define a new node in the graph, if it doesn't exist.
     *
     * @param node
     */
    addNode(node) {
        if (!this.has(node)) {
            this.forwardAdj.set(node, []);
            this.backwardAdj.set(node, []);
        }
    }
    /**
     * Define a new edge in the graph.
     *
     * @param from
     * @param to
     */
    addEdge(from, to) {
        this.addNode(from);
        this.addNode(to);
        this.forwardAdj.get(from).push(to);
        this.backwardAdj.get(to).push(from);
    }
    /**
     * Remove a node from the graph.
     *
     * @param node
     */
    removeNode(node) {
        // Delete all forward links of the node
        const children = this.forwardAdj.get(node);
        for (const child of children) {
            const childParents = this.backwardAdj.get(child);
            const parentIndex = childParents.indexOf(node);
            childParents.splice(parentIndex, 1);
        }
        this.forwardAdj.delete(node);
        // Delete all backward links of the node
        const parents = this.backwardAdj.get(node);
        for (const parent of parents) {
            const parentChildren = this.forwardAdj.get(parent);
            const childIndex = parentChildren.indexOf(node);
            parentChildren.splice(childIndex, 1);
        }
        this.backwardAdj.delete(node);
    }
    /**
     * Remove an edge from the graph.
     *
     * @param from
     * @param to
     */
    removeEdge(from, to) {
        const children = this.forwardAdj.get(from);
        const childIndex = children.indexOf(to);
        children.splice(childIndex, 1);
        const parents = this.backwardAdj.get(to);
        const parentIndex = parents.indexOf(from);
        parents.splice(parentIndex, 1);
    }
    /**
     * Check if a node exists in the graph.
     *
     * @param node
     * @returns
     */
    has(node) {
        return this.forwardAdj.has(node);
    }
    /**
     * Get the parents of a node.
     *
     * @param node
     * @returns
     */
    getParents(node) {
        var _a;
        const parents = (_a = this.backwardAdj.get(node)) !== null && _a !== void 0 ? _a : [];
        return [...parents];
    }
    /**
     * Get the children of a node.
     *
     * @param node
     * @returns
     */
    getChildren(node) {
        var _a;
        const children = (_a = this.forwardAdj.get(node)) !== null && _a !== void 0 ? _a : [];
        return [...children];
    }
    /**
     * Reverse the edges of the graph in-place.
     *
     * @returns
     */
    reverse() {
        [this.forwardAdj, this.backwardAdj] = [this.backwardAdj, this.forwardAdj];
        return this;
    }
    /**
     * Breadth-first search traversal.
     *
     * @param source
     * @param onVisit
     */
    bfs(source, visit) {
        if (!this.has(source))
            return;
        const queue = new Queue();
        const visited = new Set();
        queue.enqueue(source);
        visited.add(source);
        while (queue.length > 0) {
            const node = queue.dequeue();
            const children = this.forwardAdj.get(node);
            visit(node);
            for (const child of children) {
                if (!visited.has(child)) {
                    visited.add(child);
                    queue.enqueue(child);
                }
            }
        }
    }
    /**
     * Depth-first search traversal.
     *
     * @param source
     * @param visit
     */
    dfs(source, visit) {
        if (!this.has(source))
            return;
        const stack = [source];
        const visited = new Set();
        visited.add(source);
        while (stack.length > 0) {
            const node = stack.pop();
            const children = this.forwardAdj.get(node);
            visit(node);
            for (const child of children) {
                if (!visited.has(child)) {
                    visited.add(child);
                    stack.push(child);
                }
            }
        }
    }
    /**
     * Use Kahn's algorithm to get the topological sort of the graph (sources -> sinks).
     *
     * Returns null if the graph is cyclic.
     *
     * @returns
     */
    topologicalSort() {
        const sorted = [];
        const indegrees = new Map();
        const queue = new Queue();
        // Populate the indegrees map and queue with nodes that have no parents.
        this.backwardAdj.forEach((parents, node) => {
            const indegree = parents.length;
            indegrees.set(node, indegree);
            if (indegree === 0) {
                queue.enqueue(node);
            }
        });
        while (queue.length > 0) {
            const node = queue.dequeue();
            const children = this.forwardAdj.get(node);
            sorted.push(node);
            for (const child of children) {
                const indegree = indegrees.get(child) - 1;
                indegrees.set(child, indegree);
                if (indegree === 0) {
                    queue.enqueue(child);
                }
            }
        }
        // Cycle detected
        if (indegrees.size !== sorted.length) {
            return null;
        }
        return sorted;
    }
}
